import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';

export interface MovementData {
  id: string;
  en_display_name: string;
  is_show_move?: boolean;
  tags: string[];
  muscles: string[];
  move_types: string[];
  image_male_url?: string;
  image_female_url?: string;
  video_male_url?: string;
  video_female_url?: string;
  video_male?: string;
  video_female?: string;
  mapped_muscle_group?: string;
}

export interface GetAllMovementsResponse {
  status: string;
  data: MovementData[];
}

const getAllMovements = async (): Promise<MovementData[]> => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_ALL_MOVEMENTS}`;

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch movements');
  }

  const res: GetAllMovementsResponse = await response.json();
  const rawMovements = (res.data || []).filter((item) => item.is_show_move === true);

  return rawMovements.map((item) => {
    // 1. Pre-compute the generic mapped muscle group for the UI based primarily on tags
    const tags = (item.tags || []).map((t) => t.toLowerCase().trim());
    const mStr = (item.muscles || []).map((m) => m.toLowerCase()).join(' ');
    const tStr = tags.join(' ');

    if (tags.includes('full body') || tStr.includes('full body')) {
      item.mapped_muscle_group = 'Full Body';
    } else if (tags.includes('core') || tStr.includes('core')) {
      item.mapped_muscle_group = 'Core';
    } else if (tags.includes('upper body') || tStr.includes('upper body')) {
      item.mapped_muscle_group = 'Upper Body';
    } else if (tags.includes('lower body') || tStr.includes('lower body')) {
      item.mapped_muscle_group = 'Lower Body';
    } else if (
      mStr.match(/quad|hamstring|calv|calf|glute|leg|adductor|abductor/) ||
      tStr.match(/leg|hip/)
    ) {
      item.mapped_muscle_group = 'Lower Body';
    } else if (
      mStr.match(/chest|back|lat|trap|shoulder|deltoid|arm|bicep|tricep|forearm/) ||
      tStr.match(/push|pull|press/)
    ) {
      item.mapped_muscle_group = 'Upper Body';
    } else if (mStr.match(/abdomin|core|oblique/)) {
      item.mapped_muscle_group = 'Core';
    } else {
      item.mapped_muscle_group = 'Full Body'; // fallback
    }

    return item;
  });
};

export const useGetAllMovements = () => {
  return useQuery({
    queryKey: ['allMovements'],
    queryFn: getAllMovements,
    staleTime: 1000 * 60 * 60 * 8, // Cache exercises for 8 hours
    gcTime: 1000 * 60 * 60 * 8, // Keep in garbage collection for 8 hours
  });
};
